import React, { FC, useMemo, useState } from 'react';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import { isSameDay } from 'date-fns';
import cx from 'classnames';
// redux
import { useSelector } from 'react-redux';
import { selectorGetQuotesAll } from 'redux/dailyQuotes-service/selector';
import { createDailyQuote, deleteDailyQuote, editDailyQuote } from 'redux/dailyQuotes-service/actions';
import useThunkDispatch from 'core/customHooks/useThunkDispatch';
// hooks & constants
import { useLocales } from 'hooks/locales';
import { createEmptyLocalizedMap, LocalizedStringsMap, SUPPORTED_LANGUAGES } from 'core/constants';
// components
import ButtonDefault from '../../../../Buttons/ButtonsDefault';
import InlineDatePicker from '../../../../Pickers/InlineDatePicker';
import InputText from '../../../../FormInputs/InputText';
import InputErrorHint from '../../../../InputErrorHint';
import Loader from '../../../../Loader';
// helpers
import { dailyQuoteValidationSchema } from 'core/validation';
// types
import { IQuoteExtendedResponse, QuoteLocalizedRequest, QuoteRequest } from '@joc/api-gateway/lib/api-client';
// styles
import styles from './AddQuoteTab.module.scss';

interface IAddQuoteTabProps {
	quote?: IQuoteExtendedResponse;
	onClose: () => void;
}

interface DailyQuoteFormValues {
	messages: LocalizedStringsMap;
	startDate: Date;
}

const MAX_MESSAGE_LENGTH = 80;

export const AddQuoteTab: FC<IAddQuoteTabProps> = ({ quote, onClose }) => {
	const dispatch = useThunkDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const { localeIdByKey, languageKeyByLocaleId } = useLocales();

	const dailyQuotes = useSelector(selectorGetQuotesAll);

	const initialData = useMemo<DailyQuoteFormValues>(() => {
		const messages = createEmptyLocalizedMap();

		if (quote?.message) {
			messages.en = quote.message;
		}

		if (Array.isArray(quote?.quoteLocalized)) {
			quote!.quoteLocalized.forEach(item => {
				const langKey = languageKeyByLocaleId[item.localeId];
				if (langKey) {
					if (langKey === 'en') {
						messages.en = item.messageLocalized || messages.en;
					} else {
						messages[langKey] = item.messageLocalized || '';
					}
				}
			});
		}

		return {
			messages,
			startDate: quote?.startDate ? new Date(quote.startDate) : new Date(),
		};
	}, [quote, languageKeyByLocaleId]);

	const submitClickHandler = async (values: DailyQuoteFormValues) => {
		const { messages, startDate } = values;
		setIsLoading(true);

		const quoteLocalized: QuoteLocalizedRequest[] = [];

		SUPPORTED_LANGUAGES.forEach(lang => {
			const trimmedText = (messages[lang.key] || '').trim();
			if (trimmedText) {
				quoteLocalized.push(
					QuoteLocalizedRequest.fromJS({
						localeId: localeIdByKey[lang.key],
						messageLocalized: trimmedText,
					})
				);
			}
		});

		try {
			if (quote) {
				await dispatch(
					editDailyQuote(quote.id, {
						quoteLocalized,
						startDate,
					})
				);
			} else {
				const quotesWithSameDate = dailyQuotes.filter(q => isSameDay(new Date(q.startDate), startDate));

				await Promise.all(quotesWithSameDate.map(async q => dispatch(deleteDailyQuote(q))));

				await dispatch(
					createDailyQuote([
						QuoteRequest.fromJS({
							quoteLocalized,
							startDate,
						}),
					])
				);
			}

			onClose();
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Formik
			enableReinitialize
			initialValues={initialData}
			validationSchema={dailyQuoteValidationSchema}
			onSubmit={submitClickHandler}
		>
			{({
				setFieldValue,
				setFieldTouched,
				dirty,
				isValid,
				errors,
				touched,
			}: FormikProps<DailyQuoteFormValues>) => (
				<Form className={styles.form}>
					<div className={styles.form_languages}>
						{SUPPORTED_LANGUAGES.map(lang => {
							const fieldName = `messages.${lang.key}`;
							const inputId = `quote-message-${lang.key}`;
							return (
								<Field key={fieldName} name={fieldName}>
									{({ field, meta }: FieldProps<string, DailyQuoteFormValues>) => {
										const currentLength = (field.value || '').trim().length;
										return (
											<div className={styles.form_item}>
												<label className={styles.form_label} htmlFor={inputId}>
													Daily Quotes text ({lang.label})
													{lang.required && <span className={styles.form_required}>*</span>}
												</label>
												<InputText
													id={inputId}
													field={field}
													dir={lang.dir}
													placeholder={
														lang.required
															? 'Your Quote in English'
															: `Your Quote in ${lang.label} (optional)`
													}
													setFieldValue={setFieldValue}
													onBlur={() => setFieldTouched(fieldName, true)}
												/>

												{(meta.error && meta.touched && (
													<InputErrorHint errorText={meta.error} disableMargin={true} />
												)) || (
													<span
														className={cx(styles.form_limit, {
															[styles.form_limit_error]:
																currentLength > MAX_MESSAGE_LENGTH,
														})}
													>
														Maximum characters: {currentLength}/{MAX_MESSAGE_LENGTH}
													</span>
												)}
											</div>
										);
									}}
								</Field>
							);
						})}
					</div>

					<div className={styles.form_pickers_date}>
						<Field name="startDate">
							{({ field }: FieldProps) => (
								<div className={styles.form_item}>
									<label className={styles.form_label} htmlFor="startDay">
										Start Date<span className={styles.form_required}>*</span>
									</label>
									<InlineDatePicker
										parentClassNames={styles.form_pickers_date__item}
										fieldName={field.name}
										fieldValue={field.value}
										setFieldValue={setFieldValue}
										availableFuture={true}
									/>
									{errors.startDate && touched.startDate && (
										<InputErrorHint errorText={errors.startDate as string} />
									)}
								</div>
							)}
						</Field>
					</div>
					<div className={styles.form_footer}>
						<ButtonDefault
							classList={['secondary', 'lg']}
							parentClassName={styles.footer_button}
							title="Cancel"
							clickHandler={onClose}
						/>
						{isLoading ? (
							<Loader loadProps={{ disableCenterStyle: true, disableMargin: true }} />
						) : (
							<ButtonDefault
								submitType={true}
								classList={['primary', 'lg']}
								parentClassName={cx(styles.footer_button, {
									[styles.disable_submit]: !dirty || !isValid,
								})}
								title={quote ? 'Save' : 'Create'}
								disabled={!dirty || !isValid}
							/>
						)}
					</div>
				</Form>
			)}
		</Formik>
	);
};
